import { IconCheckOutline18, IconChevronDownOutline18, IconPlugOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";
import { SlackOAuthConnectButton } from "@/app/(v2)/connectors/_components/SlackOAuthConnectButton";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { resolveSlackUserAvatar, resolveSlackUserLabel } from "./slackUsers";
import { useSlackUsers } from "../../_hooks/useSlackUsers";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
interface SlackUserSelectorProps {
  label: string;
  description?: string;
  /** Comma-separated user IDs (matches the Slack action's `users` payload). */
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  /** Optional: pre-select a specific connector. If omitted, the selector manages workspace selection internally. */
  connectorId?: string;
  /** Optional: connector type. Defaults to "slack". */
  connectorType?: string;
  /** When true, render the current selection as static text (no interactive dropdowns). */
  disabled?: boolean;
}
function parseUserIds(value: string): string[] {
  return value.split(",").map(s => s.trim()).filter(Boolean);
}
function formatUserIds(ids: string[]): string {
  return ids.join(",");
}
export default function SlackUserSelector({
  label,
  description,
  value,
  onChange,
  required,
  connectorId: externalConnectorId,
  connectorType: externalConnectorType,
  disabled = false
}: SlackUserSelectorProps) {
  // --- Workspace selection (mirrors SlackChannelSelector) ---
  const connectors = useConnectorsStore(s => s.connectors);
  const loadConnectors = useConnectorsStore(s_0 => s_0.loadConnectors);
  const slackConnectors = useMemo(() => connectors.filter(c => c.type === "slack"), [connectors]);
  const [internalConnectorId, setInternalConnectorId] = useState<string | null>(null);
  const [workspaceDropdownOpen, setWorkspaceDropdownOpen] = useState(false);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const activeConnectorId = externalConnectorId || internalConnectorId;
  const activeConnector = useMemo(() => slackConnectors.find(c_0 => c_0.id === activeConnectorId), [slackConnectors, activeConnectorId]);
  const activeConnectorType = externalConnectorType || activeConnector?.type || "slack";
  useEffect(() => {
    if (externalConnectorId) return;
    const isStale = internalConnectorId && !slackConnectors.some(c_1 => c_1.id === internalConnectorId);
    if (slackConnectors.length === 1 && (!internalConnectorId || isStale)) {
      setInternalConnectorId(slackConnectors[0].id);
    } else if (isStale) {
      setInternalConnectorId(null);
    }
  }, [externalConnectorId, slackConnectors, internalConnectorId]);
  const handleWorkspaceChange = useCallback((id: string) => {
    setInternalConnectorId(id);
    setWorkspaceDropdownOpen(false);
    // Clear user selection when workspace changes
    onChange("");
  }, [onChange]);
  const handleSlackConnected = useCallback(() => {
    void loadConnectors();
  }, [loadConnectors]);

  // --- User selection ---
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedIds = useMemo(() => parseUserIds(value), [value]);

  // Shared via react-query (5-minute staleTime) — multiple Slack pickers on
  // the same workspace deduplicate to one network request.
  const {
    slackUsers: users,
    slackUsersMap: usersById,
    isLoading: loading,
    isError: fetchError
  } = useSlackUsers(activeConnectorId ?? undefined, activeConnectorType, !!activeConnectorId);

  // Single outside-click handler for both dropdowns. Per b3os-web CLAUDE.md
  // we don't want N per-component listeners — one effect, one listener.
  // Also clears the search query when the user dropdown closes so reopening
  // starts fresh instead of showing the previous filter.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (workspaceRef.current && !workspaceRef.current.contains(target)) {
        setWorkspaceDropdownOpen(false);
      }
      if (containerRef.current && !containerRef.current.contains(target) && !workspaceRef.current?.contains(target)) {
        setIsOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search across name, real_name, profile.display_name, and id (for paste flow).
  const filteredUsers = useMemo(() => {
    const lowerQuery = query.trim().replace(/^@/, "").toLowerCase();
    if (!lowerQuery) return users;
    return users.filter(u => {
      if (u.id.toLowerCase().includes(lowerQuery)) return true;
      if (u.name.toLowerCase().includes(lowerQuery)) return true;
      if (u.real_name?.toLowerCase().includes(lowerQuery)) return true;
      if (u.profile?.display_name?.toLowerCase().includes(lowerQuery)) return true;
      if (u.profile?.real_name?.toLowerCase().includes(lowerQuery)) return true;
      return false;
    });
  }, [users, query]);
  const handleSelect = useCallback((userId: string) => {
    const next = selectedIds.includes(userId) ? selectedIds.filter(id_0 => id_0 !== userId) : [...selectedIds, userId];
    onChange(formatUserIds(next));
  }, [selectedIds, onChange]);
  const handleRemove = useCallback((userId_0: string) => {
    onChange(formatUserIds(selectedIds.filter(id_1 => id_1 !== userId_0)));
  }, [selectedIds, onChange]);

  // --- Read-only display ---
  // Runs BEFORE the "no connector" guard so historical run views still render
  // a static list of selected user IDs even if the user later removed their
  // Slack workspace (or an admin is viewing another user's run).
  if (disabled) {
    return <div className="space-y-1">
        {label && <label className="field-label block">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>}
        <div className="text-ds-text-secondary text-sm dark:text-zinc-200">
          {selectedIds.length > 0 ?
        // Only resolve via the user map when it's actually populated.
        // resolveSlackUserLabel returns the truthy string "Slack user" for
        // any valid Slack ID even when the map is empty, which would mask
        // the raw ID and show "Slack user, Slack user" forever in run
        // history views where the connector is gone.
        selectedIds.map(id_2 => (usersById.size > 0 ? resolveSlackUserLabel(id_2, usersById) : null) || id_2).join(", ") : <span className="text-ds-text-disabled">No users selected</span>}
        </div>
      </div>;
  }

  // --- Render: No Slack workspaces connected ---
  if (!externalConnectorId && slackConnectors.length === 0) {
    return <div className="space-y-2">
        {label && <label className="field-label block">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>}
        {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
        <SlackOAuthConnectButton onSuccess={handleSlackConnected} />
      </div>;
  }
  const showWorkspaceSelector = !externalConnectorId && slackConnectors.length > 1;

  // --- Helper: workspace selector dropdown ---
  function renderWorkspaceSelector() {
    return <div className="relative" ref={workspaceRef}>
        <label className="field-label text-ds-text-muted mb-1 block text-xs">Workspace</label>
        <button type="button" onClick={() => setWorkspaceDropdownOpen(!workspaceDropdownOpen)} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm hover:border-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:hover:border-zinc-600">
          {activeConnector ? <span className="flex items-center gap-2">
              <IconPlugOutline18 className="h-4 w-4 text-blue-500" />
              <span>{activeConnector.name}</span>
            </span> : <span className="text-ds-text-disabled dark:text-ds-text-muted">Select a workspace...</span>}
          <IconChevronDownOutline18 className="text-ds-text-disabled h-4 w-4" />
        </button>
        {workspaceDropdownOpen && <div className="border-ds-border-default bg-ds-surface-card shadow-ds-elev-2 absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border">
            {slackConnectors.map(connector => <button key={connector.id} type="button" onClick={() => handleWorkspaceChange(connector.id)} className={`hover:bg-ds-surface-hover flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${connector.id === activeConnectorId ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" : "text-ds-text-primary"}`}>
                <IconPlugOutline18 className="h-4 w-4 flex-shrink-0 opacity-60" />
                <span className="min-w-0 flex-1 truncate">{connector.name}</span>
                {connector.id === activeConnectorId && <IconCheckOutline18 className="h-4 w-4 flex-shrink-0 text-blue-500" />}
              </button>)}
          </div>}
      </div>;
  }
  return <div className="space-y-2" ref={containerRef}>
      {label && <label className="field-label block">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      {showWorkspaceSelector && renderWorkspaceSelector()}

      {!activeConnectorId && showWorkspaceSelector ? <p className="text-ds-text-muted text-xs">Select a workspace to see users</p> : <>
          {/* Selected user chips */}
          {selectedIds.length > 0 && <div className="flex flex-wrap gap-1.5 pb-1">
              {selectedIds.map(id_3 => {
          const u_0 = usersById.get(id_3);
          // Same guard as the disabled view: skip resolveSlackUserLabel
          // when the map is empty so chips show the raw ID instead of
          // briefly flashing "@Slack user" while the user list loads.
          const chipLabel = (usersById.size > 0 ? resolveSlackUserLabel(id_3, usersById) : null) || id_3;
          const avatar = resolveSlackUserAvatar(id_3, usersById);
          return <span key={id_3} className="inline-flex items-center gap-1 rounded-md bg-blue-50 py-0.5 pl-1 pr-2 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    {avatar ?
            // eslint-disable-next-line @next/next/no-img-element
            <img src={avatar} alt={chipLabel} className="h-4 w-4 rounded" /> : <span className="bg-ds-neutral-200 dark:bg-ds-neutral-700 inline-block h-4 w-4 rounded" />}
                    <span>@{u_0?.name || chipLabel}</span>
                    <button type="button" onClick={e_0 => {
              e_0.stopPropagation();
              handleRemove(id_3);
            }} className="ml-0.5 text-blue-400 hover:text-blue-600 dark:text-blue-500 dark:hover:text-blue-300" aria-label={`Remove ${chipLabel}`}>
                      <IconXmarkOutline18 className="h-3 w-3" />
                    </button>
                  </span>;
        })}
            </div>}

          <div className="relative">
            {activeConnector && slackConnectors.length === 1 && <p className="text-ds-text-muted mb-1 text-xs">
                Workspace: <span className="text-ds-text-secondary font-medium">{activeConnector.name}</span>
              </p>}

            {isOpen ? <Input type="text" value={query} onChange={e_1 => setQuery(e_1.target.value)} placeholder="Search users by name..." autoFocus className="text-ds-text-primary dark:bg-ds-neutral-800 border-blue-500 ring-2 ring-blue-500/20 dark:border-blue-500" /> : <button type="button" onClick={() => setIsOpen(true)} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm hover:border-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:hover:border-zinc-600">
                <span className="text-ds-text-disabled dark:text-ds-text-muted">
                  {selectedIds.length > 0 ? `${selectedIds.length} selected — click to add more` : "Select users..."}
                </span>
                <IconChevronDownOutline18 className="text-ds-text-disabled h-4 w-4" />
              </button>}

            {isOpen && <div className="border-ds-border-default bg-ds-surface-card shadow-ds-elev-2 absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border">
                {loading ? <div className="text-ds-text-disabled flex items-center gap-2 px-3 py-2 text-sm">
                    <PixelLoader size="sm" />
                    Loading users...
                  </div> : fetchError ? <div className="text-ds-text-disabled px-3 py-2 text-sm">
                    Could not load users. Make sure the bot has the <code>users:read</code> scope.
                  </div> : filteredUsers.length === 0 ? <div className="text-ds-text-disabled px-3 py-2 text-sm">
                    {query ? "No users match your search" : "No users found"}
                  </div> : filteredUsers.map(u_1 => {
            const isSelected = selectedIds.includes(u_1.id);
            const displayName = u_1.profile?.display_name || u_1.real_name || u_1.name;
            const avatar_0 = u_1.profile?.image_32 || u_1.profile?.image_24;
            return <button key={u_1.id} type="button" onClick={() => handleSelect(u_1.id)} className={`hover:bg-ds-surface-hover flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${isSelected ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" : "text-ds-text-primary"}`}>
                        <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${isSelected ? "border-blue-500 bg-blue-500 text-white" : "border-ds-neutral-300 dark:border-zinc-600"}`}>
                          {isSelected && <IconCheckOutline18 className="h-3 w-3 text-white" />}
                        </span>
                        {avatar_0 ?
              // eslint-disable-next-line @next/next/no-img-element
              <img src={avatar_0} alt={displayName} className="h-5 w-5 shrink-0 rounded" /> : <span className="bg-ds-neutral-200 dark:bg-ds-neutral-700 inline-block h-5 w-5 shrink-0 rounded" />}
                        <span className="min-w-0 flex-1 truncate">
                          <span className="font-medium">{displayName}</span>
                          {u_1.name !== displayName && <span className="text-ds-text-muted ml-1.5 text-xs">@{u_1.name}</span>}
                        </span>
                        {u_1.is_admin && <span className="bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-700 shrink-0 rounded px-1.5 py-0.5 text-[10px]">
                            Admin
                          </span>}
                      </button>;
          })}
              </div>}
          </div>
        </>}
    </div>;
}